import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";

const PrivacyPolicy = () => {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy — Datalevo Tools | Walking Weight Loss Calculator</title>
        <meta
          name="description"
          content="Read Datalevo's privacy policy. Learn how we protect your data with client-side processing and our commitment to privacy-first tools."
        />
        <link rel="canonical" href="https://walkingweightlosscalculator.datalevo.com/privacy-policy" />
      </Helmet>

      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground mb-8">
            Last updated: December 4, 2025
          </p>

          <Card className="p-6 md:p-8">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Overview</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Datalevo ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our Walking Weight Loss Calculator and related services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Client-Side Processing</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong className="text-foreground">Our tools are privacy-first by design.</strong> All calculations for the Walking Weight Loss Calculator are performed locally in your web browser. This means:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Your personal health data (weight, height, age, etc.) is processed entirely on your device</li>
                  <li>No calculation data is transmitted to our servers by default</li>
                  <li>Your health metrics remain private unless you explicitly choose to share them</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Data We Collect</h2>
                
                <h3 className="text-lg font-medium text-foreground mt-4 mb-2">Automatically Collected Data</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you visit our website, we may collect basic analytics data including: pages visited, time spent on pages, browser type, device type, and approximate geographic location (country/region level). This data is anonymized and used to improve our services.
                </p>

                <h3 className="text-lg font-medium text-foreground mt-4 mb-2">User-Provided Data (Optional)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  If you choose to use optional features, we may collect:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Shareable Links:</strong> If you create a shareable link, your calculation results are stored on our servers with a unique code. These links expire after 7 days and are automatically deleted.</li>
                  <li><strong>Feedback:</strong> If you submit feedback (like/dislike), we store your feedback type and optional comments. No personally identifiable information is attached to feedback.</li>
                  <li><strong>Contact Information:</strong> If you email us, we retain your email address and message to respond to your inquiry.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Shareable Links & Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Shareable links have a Time-To-Live (TTL) of 7 days. After this period, the associated data is automatically deleted from our servers. You can also delete your shared link at any time by using the delete option provided when the link is created.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Cookies</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use minimal cookies to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Remember your theme preference (light/dark mode)</li>
                  <li>Provide basic analytics functionality</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  We do not use tracking cookies for advertising purposes. You can disable cookies in your browser settings.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Analytics (Opt-In)</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Analytics features are opt-in. If enabled, we track aggregated usage data to improve our tools. Events tracked may include: tool usage, feature engagement, and error occurrences. We never track personally identifiable health information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Third-Party Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may use third-party services for hosting, analytics, and infrastructure. These services have their own privacy policies and we ensure they meet our privacy standards before integration.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Access the data we hold about you</li>
                  <li>Request deletion of your data</li>
                  <li>Opt out of analytics tracking</li>
                  <li>Delete shareable links you've created</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are intended for adults aged 18 and older. We do not knowingly collect information from children under 18. If you believe a child has provided us with personal information, please contact us immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy periodically. We will notify you of significant changes by posting a notice on our website. Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Privacy Policy or our data practices, please contact us at:
                </p>
                <p className="text-muted-foreground mt-2">
                  <strong className="text-foreground">Email:</strong>{" "}
                  <a href="mailto:contact@datalevo.com" className="text-primary hover:underline">
                    contact@datalevo.com
                  </a>
                </p>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
